/**
 * SupportDrawer - Right-side drawer for Support AI
 * Opens from Assist buttons across the app with pre-filled context
 * P1: Includes guided flows for setup tasks
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Bot, User, Copy, Check, ExternalLink, RefreshCw, ChevronRight,
         MessageSquare, Settings, Sparkles, HelpCircle, Loader2, Users, Upload, 
         BookOpen, ArrowLeft, Activity } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSupportContext, SUPPORT_ROLES } from '../context/SupportContext';
import GuidedFlowPanel from './GuidedFlowPanel';
import DiagnosticsPanel from './DiagnosticsPanel';
import { toast } from 'sonner';

// Tab definitions
const TABS = [
  { id: 'advisory', label: 'Advisory', icon: Sparkles, description: 'Scope, estimation & best practices' },
  { id: 'product', label: 'Product Support', icon: Settings, description: 'Setup, troubleshooting & config' }
];

// Guided flow options for Product Support
const GUIDED_FLOWS = [
  { id: 'rbac', label: 'Users & RBAC Setup', icon: Users, description: 'Configure users, roles, and groups' },
  { id: 'upload', label: 'Upload Take-on', icon: Upload, description: 'Validate and import Excel data' },
  { id: 'diagnostics', label: 'System Diagnostics', icon: Activity, description: 'Check system health & configuration' }
];

// Suggested prompts per tab
const SUGGESTED_PROMPTS = {
  advisory: [
    { label: 'Identify scope risks', prompt: 'What are the key risks in this scope that need mitigation?' },
    { label: 'Estimation guidance', prompt: 'What is a realistic timeline for delivering this scope?' },
    { label: 'Best practices', prompt: 'What governance checkpoints should we include?' },
    { label: 'Break down epic', prompt: 'Help me break down this epic into more granular features.' },
  ],
  product: [
    { label: 'Upload help', prompt: 'How do I fix upload errors in my Excel template?' },
    { label: 'User permissions', prompt: 'How do I add a new user and assign them a role?' },
    { label: 'Teams setup', prompt: 'How do I configure Microsoft Teams integration?' },
    { label: 'Daily Signal', prompt: 'How do I set up Daily Signal notifications?' },
  ]
};

// Chat message component
const ChatMessage = ({ message, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
        isUser ? 'bg-[var(--clr-accent)]' : 'bg-gradient-to-br from-purple-500 to-blue-500'
      } text-white`}>
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block p-3 rounded-xl text-sm ${
          isUser 
            ? 'bg-[var(--clr-accent)] text-white rounded-tr-sm' 
            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
        }`}>
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>
        
        {!isUser && (
          <div className="mt-1 flex items-center gap-2">
            <button onClick={handleCopy} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            {message.timestamp && (
              <span className="text-xs text-gray-400">
                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        )}
        
        {/* Quick Actions */}
        {message.quickActions && message.quickActions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (action.action === 'link' && action.params?.href) {
                    window.location.href = action.params.href;
                  }
                }}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {action.label}
              </button>
            ))}
          </div>
        )}
        
        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            <span className="font-medium">Sources:</span>{' '}
            {message.citations.map((c, idx) => (
              <span key={idx}>
                {c.title}{idx < message.citations.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SupportDrawer = () => {
  const { user } = useAuth();
  const {
    isDrawerOpen,
    closeSupportDrawer,
    drawerMode,
    initialMessage,
    buildSupportContext,
    preferredRole,
    setPreferredRole,
    selectedProjectId,
    setSelectedProjectId,
    entityContext
  } = useSupportContext();
  
  const [activeTab, setActiveTab] = useState(drawerMode || 'advisory');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [activeGuidedFlow, setActiveGuidedFlow] = useState(null); // P1: Track active guided flow
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Focus input when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Set initial message if provided
      if (initialMessage) {
        setInputValue(initialMessage);
      }
    }
  }, [isDrawerOpen, initialMessage]);
  
  // Sync tab with drawerMode
  useEffect(() => {
    if (drawerMode) {
      setActiveTab(drawerMode);
    }
  }, [drawerMode]);
  
  // Load projects
  useEffect(() => {
    if (isDrawerOpen && tenantId && user?.id) {
      loadProjects();
      loadPreferences();
    }
  }, [isDrawerOpen, tenantId, user?.id]);
  
  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await api.getSupportProjects(tenantId, user.id);
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };
  
  const loadPreferences = async () => {
    try {
      const res = await api.getSupportPreferences(tenantId, user.id);
      if (res.data.preferred_role) {
        setPreferredRole(res.data.preferred_role);
      }
      if (res.data.last_project_id) {
        setSelectedProjectId(res.data.last_project_id);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };
  
  const savePreferences = async (role, projectId) => {
    try {
      await api.updateSupportPreferences(tenantId, user.id, {
        preferred_role: role,
        last_project_id: projectId
      });
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };
  
  const handleRoleChange = (roleKey) => {
    setPreferredRole(roleKey);
    setShowRoleSelector(false);
    savePreferences(roleKey, selectedProjectId);
    toast.success(`Role set to ${SUPPORT_ROLES.find(r => r.key === roleKey)?.label}`);
  };
  
  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    savePreferences(preferredRole, projectId);
  };
  
  const handleSend = async (prompt = inputValue) => {
    if (!prompt.trim() || sending) return;
    
    const userMessage = {
      role: 'user',
      content: prompt.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setSending(true);
    
    try {
      const supportContext = buildSupportContext({
        projectId: selectedProjectId
      });
      
      const res = await api.supportAssist(tenantId, user.id, {
        mode: activeTab,
        message: prompt.trim(),
        supportContext
      });
      
      const assistantMessage = {
        role: 'assistant',
        content: res.data.answerHtml,
        timestamp: new Date().toISOString(),
        quickActions: res.data.quickActions,
        links: res.data.links,
        citations: res.data.citations
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Support assist failed:', err);
      const errorMsg = err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to get response';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I encountered an error: ${errorMsg}. Please try again.`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setSending(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const clearChat = () => {
    setMessages([]);
  };
  
  if (!isDrawerOpen) return null;
  
  const currentRole = SUPPORT_ROLES.find(r => r.key === preferredRole);
  const currentProject = projects.find(p => p.id === selectedProjectId);
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={closeSupportDrawer}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[440px] bg-white shadow-2xl z-50 flex flex-col"
           data-testid="support-drawer">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Support AI</h2>
                <p className="text-xs text-gray-500">How can I help?</p>
              </div>
            </div>
            <button onClick={closeSupportDrawer} className="p-1.5 hover:bg-gray-200 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`support-tab-${tab.id}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Selectors Row */}
        <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 flex gap-2">
          {/* Role Selector */}
          <div className="relative flex-1">
            <button
              onClick={() => setShowRoleSelector(!showRoleSelector)}
              className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-left flex items-center justify-between hover:border-gray-300"
              data-testid="support-role-selector"
            >
              <span className="text-gray-600">
                {currentRole ? currentRole.label : 'Select role...'}
              </span>
              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showRoleSelector ? 'rotate-90' : ''}`} />
            </button>
            
            {showRoleSelector && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                {SUPPORT_ROLES.map(role => (
                  <button
                    key={role.key}
                    onClick={() => handleRoleChange(role.key)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 ${
                      preferredRole === role.key ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{role.label}</div>
                    <div className="text-gray-500">{role.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Project Selector */}
          <select
            value={selectedProjectId || ''}
            onChange={(e) => handleProjectChange(e.target.value)}
            className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
            data-testid="support-project-selector"
          >
            <option value="">Select project...</option>
            {loadingProjects ? (
              <option disabled>Loading...</option>
            ) : projects.length === 0 ? (
              <option disabled>No projects available</option>
            ) : (
              projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))
            )}
          </select>
        </div>
        
        {/* Context Banner (when entity is selected) */}
        {entityContext?.entityType && (
          <div className="flex-shrink-0 px-4 py-2 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>
                Context: <span className="font-medium capitalize">{entityContext.entityType}</span>
                {entityContext.entityId && ` (${entityContext.entityId.substring(0, 8)}...)`}
              </span>
            </div>
          </div>
        )}
        
        {/* Guided Flow Panel (P1) - shows instead of chat when active */}
        {activeGuidedFlow && activeGuidedFlow !== 'diagnostics' && (
          <div className="flex-1 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50">
              <button 
                onClick={() => setActiveGuidedFlow(null)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ArrowLeft className="w-4 h-4 text-gray-500" />
              </button>
              <span className="text-xs font-medium text-gray-700">Back to chat</span>
            </div>
            <GuidedFlowPanel 
              flowType={activeGuidedFlow} 
              onClose={() => setActiveGuidedFlow(null)}
            />
          </div>
        )}
        
        {/* Diagnostics Panel (P1.5) */}
        {activeGuidedFlow === 'diagnostics' && (
          <div className="flex-1 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50">
              <button 
                onClick={() => setActiveGuidedFlow(null)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ArrowLeft className="w-4 h-4 text-gray-500" />
              </button>
              <span className="text-xs font-medium text-gray-700">Back to chat</span>
            </div>
            <DiagnosticsPanel onClose={() => setActiveGuidedFlow(null)} />
          </div>
        )}
        
        {/* Chat Area - hidden when guided flow is active */}
        {!activeGuidedFlow && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {/* Guided Flows Section (Product Support only) */}
            {activeTab === 'product' && messages.length === 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Setup Guides</p>
                <div className="grid gap-2">
                  {GUIDED_FLOWS.map((flow) => (
                    <button
                      key={flow.id}
                      onClick={() => setActiveGuidedFlow(flow.id)}
                      className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                      data-testid={`guided-flow-${flow.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                          <flow.icon className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">{flow.label}</div>
                          <div className="text-xs text-gray-500">{flow.description}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggestions (when empty) */}
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 text-center">
                  {activeTab === 'advisory' 
                    ? 'Ask about scope, estimation, and best practices'
                    : 'Or ask a question directly'}
                </p>
                <div className="grid gap-2">
                  {SUGGESTED_PROMPTS[activeTab].map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(item.prompt)}
                      className="w-full text-left p-2.5 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                      data-testid={`suggested-prompt-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-3.5 h-3.5 text-purple-500" />
                        <span className="text-xs text-gray-700">{item.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Messages */}
            {messages.map((msg, idx) => (
              <ChatMessage key={idx} message={msg} />
            ))}
            
            {/* Typing indicator */}
            {sending && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-gray-100 rounded-xl rounded-tl-sm px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
        
        {/* Input Area - hidden when guided flow is active */}
        {!activeGuidedFlow && (
          <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="w-full mb-2 text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Clear chat
              </button>
            )}
            
            <div className="relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question..."
                rows={2}
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={sending}
                data-testid="support-chat-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || sending}
                className="absolute right-2 bottom-2 p-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
                data-testid="support-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            
            <p className="mt-2 text-xs text-gray-400 text-center">
              Press Enter to send · AI suggestions - verify with your team
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default SupportDrawer;
